import moment from 'moment';

import {getStoragePeriod} from 'helpers/localStorage/DatePeriodStorage';
import {
  ANALYTICS_FULFILLMENT_LABELS,
  ANALYTICS_ITEMS_PAGE_SIZE,
  ANALYTICS_CHANNEL_LABELS
} from 'helpers/constants/analytics';

const HOURS_IN_DAY = 24;

export const ANALYTICS_DATE_PERIOD_STORAGE_NAME = 'DatePeriod';
export const ANALYTICS_ALL_PERIOD_INDEX = 0;

// The BFF expects location-local wall clock timestamps without a timezone suffix.
export const formatAnalyticsDate = (value) =>
  value ? moment.parseZone(value).format('YYYY-MM-DDTHH:mm:ss') : value;

export const isAnalyticsAllPeriod = () =>
  getStoragePeriod(ANALYTICS_DATE_PERIOD_STORAGE_NAME) ===
  ANALYTICS_ALL_PERIOD_INDEX;

export const resolveAnalyticsRequestDates = (dateRange) => {
  if (isAnalyticsAllPeriod()) {
    return {startDate: null, endDate: null};
  }

  return {
    startDate: dateRange?.[0] ?? null,
    endDate: dateRange?.[1] ?? null
  };
};

export const hasAnalyticsDateRange = (dateRange) => {
  if (isAnalyticsAllPeriod()) {
    return true;
  }

  return Boolean(dateRange?.[0] && dateRange?.[1]);
};

export const unwrapAnalyticsPayload = (response) =>
  response?.data?.data ?? response?.data ?? response;

const SORTING_TO_BFF = {
  rank: 'Rank',
  name: 'Name',
  ordersAmount: 'OrdersAmount',
  salesAmount: 'SalesAmount',
  totalOrders: 'OrdersAmount',
  totalSales: 'SalesAmount',
  Rank: 'Rank',
  Name: 'Name',
  OrdersAmount: 'OrdersAmount',
  SalesAmount: 'SalesAmount'
};

const mapSortingKeyToBff = (sortingKey) =>
  SORTING_TO_BFF[sortingKey] || SORTING_TO_BFF.rank;

const mapSortingDirectionToBff = (sortingDirection) => {
  if (!sortingDirection) {
    return undefined;
  }

  const normalized = String(sortingDirection).toLowerCase();
  return normalized === 'asc' || normalized === 'desc' ? normalized : undefined;
};

export const buildAnalyticsRequest = ({
  startDate,
  endDate,
  channelTypes,
  fulfillmentModes,
  includeSalesPerItem = false,
  includeKpi = true,
  includePerHoursStatistics = true,
  includeSalesCountSeries = false,
  paging = 1,
  pageSize = ANALYTICS_ITEMS_PAGE_SIZE,
  sortingKey,
  sortingDirection
} = {}) => ({
  from: formatAnalyticsDate(startDate),
  to: formatAnalyticsDate(endDate),
  channelTypes: channelTypes ?? [],
  fulfillmentModes: fulfillmentModes ?? [],
  includeKpi,
  includePerHoursStatistics,
  includeSalesCountSeries,
  salesPerItem: includeSalesPerItem
    ? {
        paging,
        pageSize,
        sortingKey: mapSortingKeyToBff(sortingKey),
        sortingDirection: mapSortingDirectionToBff(sortingDirection)
      }
    : null
});

export const buildAnalyticsSalesPerItemRequest = ({
  startDate,
  endDate,
  channelTypes,
  fulfillmentModes,
  paging = 1,
  pageSize = ANALYTICS_ITEMS_PAGE_SIZE,
  sortingKey,
  sortingDirection
} = {}) => ({
  from: formatAnalyticsDate(startDate),
  to: formatAnalyticsDate(endDate),
  channelTypes: channelTypes ?? [],
  fulfillmentModes: fulfillmentModes ?? [],
  paging,
  pageSize,
  sortingKey: mapSortingKeyToBff(sortingKey),
  sortingDirection: mapSortingDirectionToBff(sortingDirection)
});

const getSalesCount = (payload) =>
  payload?.salesCount ?? payload?.SalesCount ?? null;

const getCustomersCount = (payload) =>
  payload?.customersCount ?? payload?.CustomersCount ?? null;

const getSalesPerHour = (payload) =>
  payload?.salesPerHour ?? payload?.SalesPerHour ?? null;

const normalizeSalesCountSeries = (series) => {
  if (!series) {
    return null;
  }

  const points = (series.points ?? series.Points ?? []).map((point) => ({
    bucketStart: point.bucketStart ?? point.BucketStart,
    grossSales: Number(point.grossSales ?? point.GrossSales) || 0,
    orderVolume: Number(point.orderVolume ?? point.OrderVolume) || 0
  }));

  return {
    granularity: series.granularity ?? series.Granularity ?? 'day',
    points
  };
};

export const normalizeSalesCount = (payload) => {
  const salesCount = getSalesCount(payload);
  if (!salesCount) {
    return {};
  }

  const summary = salesCount.summary ?? salesCount.Summary ?? {};

  return {
    totalSum: Number(summary.grossSales ?? summary.GrossSales) || 0,
    orders: Number(summary.orderVolume ?? summary.OrderVolume) || 0,
    avgOrderSize: Number(summary.avgOrderValue ?? summary.AvgOrderValue) || 0,
    series: normalizeSalesCountSeries(salesCount.series ?? salesCount.Series)
  };
};

export const normalizeCustomersCount = (payload) => {
  const customersCount = getCustomersCount(payload);
  if (!customersCount) {
    return {};
  }

  return {
    totalCustomers:
      Number(customersCount.totalAmount ?? customersCount.TotalAmount) || 0,
    avgOrderAmount:
      Number(customersCount.avgOrderAmount ?? customersCount.AvgOrderAmount) ||
      0,
    avgItemsPerOrder:
      Number(
        customersCount.avgItemsPerOrder ?? customersCount.AvgItemsPerOrder
      ) || 0
  };
};

export const analyticsSeriesKey = (index) => `series_${index}`;

export const emptySalesPerHourGroup = () => ({
  series: [],
  hours: [],
  avgSales: 0
});

/**
 * Turns one BFF series list (per-channel or per-fulfillment) into a
 * recharts-friendly shape:
 * - series: [{key, name, label, color}]
 * - hours: 24 rows keyed by series key + a combined `sales` total
 * - avgSales: average combined sales per hour
 */
const normalizeSalesPerHourGroup = (groupSeries, labels) => {
  const series = groupSeries.map((entry, index) => {
    const name = entry.name ?? entry.Name ?? '';
    return {
      key: analyticsSeriesKey(index),
      name,
      label: labels[name] ?? name,
      color: entry.color ?? entry.Color ?? ''
    };
  });

  const salesByHourPerSeries = groupSeries.map((entry) => {
    const points = entry.points ?? entry.Points ?? [];
    const byHour = new Map();
    points.forEach((point) => {
      const hour = Number(point.hour ?? point.Hour);
      byHour.set(hour, Number(point.sales ?? point.Sales) || 0);
    });
    return byHour;
  });

  const hours = Array.from({length: HOURS_IN_DAY}, (_, hour) => {
    const row = {name: hour, sales: 0};
    salesByHourPerSeries.forEach((byHour, index) => {
      const value = byHour.get(hour) || 0;
      row[analyticsSeriesKey(index)] = value;
      row.sales += value;
    });
    return row;
  });

  const totalSales = hours.reduce((total, row) => total + row.sales, 0);

  return {
    series,
    hours,
    avgSales: totalSales / HOURS_IN_DAY
  };
};

/**
 * Normalizes both sales-per-hour groupings returned by the BFF
 * (GROW-2476): fulfillment modes and sales channels.
 */
export const normalizeSalesPerHour = (payload) => {
  const salesPerHour = getSalesPerHour(payload);
  const perFulfillment =
    salesPerHour?.perFulfillment ?? salesPerHour?.PerFulfillment ?? [];
  const perChannel = salesPerHour?.perChannel ?? salesPerHour?.PerChannel ?? [];

  return {
    perFulfillment: normalizeSalesPerHourGroup(
      perFulfillment,
      ANALYTICS_FULFILLMENT_LABELS
    ),
    perChannel: normalizeSalesPerHourGroup(perChannel, ANALYTICS_CHANNEL_LABELS)
  };
};

export const normalizeSalesPerItem = (
  payload,
  pageSize = ANALYTICS_ITEMS_PAGE_SIZE
) => {
  const salesPerItem =
    payload?.salesPerItem ?? payload?.SalesPerItem ?? payload;
  if (!salesPerItem) {
    return {
      items: [],
      totalCount: 0
    };
  }

  const items = salesPerItem.items ?? salesPerItem.Items ?? [];
  const totalPages = salesPerItem.totalPage ?? salesPerItem.TotalPage ?? 1;
  const totalCount =
    salesPerItem.totalCount ?? salesPerItem.TotalCount ?? totalPages * pageSize;

  return {
    items: items.map((item, index) => ({
      rank: item.rank ?? item.Rank ?? index + 1,
      name: item.name ?? item.Name ?? '',
      img: item.image ?? item.Image,
      totalOrders: item.ordersAmount ?? item.OrdersAmount ?? 0,
      totalSales: item.salesAmount ?? item.SalesAmount ?? 0
    })),
    totalCount
  };
};
